import { FC, useContext, useEffect } from 'react';
import nprogress from 'nprogress';
import { NotFoundBoundary, useCurrentRoute } from 'react-navi';
import { useTranslation } from 'react-i18next';
import { useLoadingRoute } from 'react-navi';
import { CommonContext, isMobileDevice } from '@repo-level/core-front';
import platform from 'platform';

import { NotFoundLayout } from 'components/internal/NotFound';
import { GlobalTooltip } from 'modules/globalTooltip';
import { useJackpotWinnerModal } from 'hooks/jackpotWinner';
import { useDeepCompareMemo } from 'hooks/common';
import { useAnalytics } from 'helpers/analytics';
import { AnalyticsEventName } from 'api/analytics';

import 'nprogress/nprogress.css';

const device = {
  deviceType: isMobileDevice ? 'mobile' : 'desktop',
  deviceName: platform.product,
  os: platform.os?.family,
  osVersion: platform.os?.version,
  browser: platform.name,
  browserVersion: platform.version,
  screenSizeWidth: window.screen.width,
  screenSizeHeight: window.screen.height,
};

const useTitleSetter = () => {
  const { title } = useCurrentRoute();

  const { t } = useTranslation();

  useEffect(() => {
    switch (typeof title) {
      case 'function': {
        document.title = title(t);
        break;
      }
      case 'string': {
        document.title = title;
        break;
      }
      default: {
        document.title = 'Luckyelfcasino';
      }
    }
  }, [title, t]);
};

const useLoadingBar = () => {
  const routeLoading = !!useLoadingRoute();

  useEffect(() => {
    nprogress.configure({
      parent: 'body',
      easing: 'ease-in-out',
      speed: 400,
      showSpinner: false,
    });

    return () => {
      nprogress.remove();
    };
  }, []);

  useEffect(() => {
    if (routeLoading) {
      nprogress.start();

      const interval = setInterval(() => {
        nprogress.inc(0.3 * Math.cos((nprogress.status || 0) * (Math.PI / 2)));
      }, 300);

      return () => clearInterval(interval);
    } else {
      nprogress.done();
    }
  }, [routeLoading]);

  return routeLoading;
};

const useNewSession = () => {
  const { currentIp } = useContext(CommonContext);
  const { sendEventToAnalytics } = useAnalytics();

  const memoizedLocation = useDeepCompareMemo(currentIp);

  useEffect(() => {
    if (!memoizedLocation) return;

    sendEventToAnalytics({
      name: AnalyticsEventName.NEW_SESSION,
      attributes: { device, location: memoizedLocation },
    });
  }, [sendEventToAnalytics, memoizedLocation]);
};

export const Layout: FC = ({ children }) => {
  useTitleSetter();

  useLoadingBar();

  useJackpotWinnerModal();

  useNewSession();

  return (
    <>
      <GlobalTooltip />
      <NotFoundBoundary render={NotFoundLayout}>{children}</NotFoundBoundary>
    </>
  );
};
